import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadModel } from 'store/actions/rpcActions';
import { clearMessages } from 'store/actions/appActions';
import {
  clearJob,
  openModel,
  pollJob,
  resolveModelJob,
  runDemoJob,
  setPollStartedAt,
} from 'store/actions/jobActions';
import constants from 'constants';
import _ from 'lodash';

const {
  JOB_PHASES,
  JOB_POLLING,
  DEFAULT_MODEL_TITLE,
  DEMO_GUID,
} = constants;

const IN_FLIGHT_PHASES = [
  JOB_PHASES.WAITING_FOR_FILE,
  JOB_PHASES.QUEUED,
  JOB_PHASES.PROCESSING,
];

const formatElapsed = ms => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (!minutes) return `${seconds}s elapsed`;
  return `${minutes}m ${seconds}s elapsed`;
};

/**
 * Owns the guid -> download -> job-status chain that every integration route
 * shares. A download 404 means the requested format has not been generated
 * yet, so instead of dead-ending the user we resolve the conversion job behind
 * the model and hand back a phase the status page can render.
 */
const useModelLoader = args => {
  const { guid, format, token, enabled = true } = args;

  const dispatch = useDispatch();
  const canvasStore = useSelector(state => state.canvas);
  const jobStore = useSelector(state => state.job);
  const guidURL = useMemo(() => canvasStore.guidURL, [ canvasStore ]);
  const [ isDownloading, setIsDownloading ] = useState(false);
  const [ now, setNow ] = useState(() => Date.now());

  const { phase, job, model, jobGuid, pollStartedAt } = jobStore;
  const showStatus = phase !== JOB_PHASES.IDLE;
  const inFlight = IN_FLIGHT_PHASES.includes(phase);
  // One hardcoded guid replays the lifecycle for demos. Nothing else changes.
  const isDemo = guid === DEMO_GUID;

  const startDownload = useCallback(() => {
    setIsDownloading(true);

    // The demo model downloads fine, so there is no 404 to trigger the chain —
    // the scripted playback stands in for the whole download step.
    if (isDemo) {
      dispatch(runDemoJob({ guid, format }));
      return;
    }

    const downloadArgs = {
      guid,
      format,
      token,
      onNotFound: () => dispatch(resolveModelJob({ guid, format })),
      onRetry: () => {
        dispatch(clearMessages());
        setIsDownloading(false);
      },
    };

    dispatch(downloadModel(downloadArgs));
  }, [ guid, format, token, isDemo, dispatch ]);

  useEffect(() => {
    const idle = phase === JOB_PHASES.IDLE;
    if (!enabled || !guid || !idle || !_.isEmpty(guidURL) || isDownloading) return;

    startDownload();
  }, [ enabled, guid, guidURL, isDownloading, phase, startDownload ]);

  // The slice is keyed off a single job, so leaving the route drops it.
  useEffect(() => {
    return () => {
      dispatch(clearJob());
    };
  }, [ dispatch ]);

  // Elapsed time and the "taking longer" thresholds read off the job's own
  // created_at; a clock that is behind the server would otherwise show nothing.
  const jobStartedAt = useMemo(() => {
    const parsed = Date.parse(job?.created_at);
    if (isNaN(parsed)) return null;
    return parsed;
  }, [ job ]);

  const elapsedMs = useMemo(() => {
    const reference = jobStartedAt ?? pollStartedAt;
    if (!reference) return null;

    const elapsed = now - reference;
    // A created_at in the future is unusable — hide it rather than count down.
    if (elapsed < 0) return null;
    return elapsed;
  }, [ jobStartedAt, pollStartedAt, now ]);

  const longWait = Boolean(elapsedMs) && elapsedMs >= JOB_POLLING.LONG_WAIT_AFTER;

  const stalled = useMemo(() => {
    if (!pollStartedAt) return false;
    return now - pollStartedAt >= JOB_POLLING.HARD_STOP_AFTER;
  }, [ pollStartedAt, now ]);

  // One ticker drives the elapsed line, the 5 minute copy and the hard stop.
  useEffect(() => {
    if (!showStatus) return;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [ showStatus ]);

  const runPoll = useCallback(() => {
    dispatch(pollJob({ jobGuid, guid, format }));
  }, [ dispatch, jobGuid, guid, format ]);

  useEffect(() => {
    if (!inFlight || !jobGuid) return;

    let timeout;

    const schedule = () => {
      const elapsed = Date.now() - (pollStartedAt || Date.now());
      // Past the hard stop the user drives it with Check again.
      if (elapsed >= JOB_POLLING.HARD_STOP_AFTER) return;

      const interval = elapsed >= JOB_POLLING.SLOW_AFTER
        ? JOB_POLLING.SLOW_INTERVAL
        : JOB_POLLING.FAST_INTERVAL;

      timeout = setTimeout(tick, interval);
    };

    // A backgrounded embed should not hammer the API.
    const tick = () => {
      if (document.visibilityState !== 'hidden') runPoll();
      schedule();
    };

    schedule();

    return () => clearTimeout(timeout);
  }, [ inFlight, jobGuid, pollStartedAt, runPoll ]);

  useEffect(() => {
    if (!inFlight || !jobGuid) return;

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const elapsed = Date.now() - (pollStartedAt || Date.now());
      if (elapsed >= JOB_POLLING.HARD_STOP_AFTER) return;

      runPoll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [ inFlight, jobGuid, pollStartedAt, runPoll ]);

  const onOpen = useCallback(() => {
    dispatch(openModel());
  }, [ dispatch ]);

  const onRetry = useCallback(() => {
    if (isDemo) {
      dispatch(runDemoJob({ guid, format }));
      return;
    }

    dispatch(resolveModelJob({ guid, format }));
  }, [ dispatch, guid, format, isDemo ]);

  const onCheckAgain = useCallback(() => {
    dispatch(setPollStartedAt(Date.now()));
    runPoll();
  }, [ dispatch, runPoll ]);

  const statusProps = useMemo(() => ({
    phase,
    title: model?.title || job?.filename || DEFAULT_MODEL_TITLE,
    thumbnailURL: model?.preview512URL,
    elapsedLabel: elapsedMs === null ? null : formatElapsed(elapsedMs),
    longWait,
    stalled,
    onOpen,
    onRetry,
    onCheckAgain,
  }), [
    phase,
    model,
    job,
    elapsedMs,
    longWait,
    stalled,
    onOpen,
    onRetry,
    onCheckAgain,
  ]);

  return { previewURL: guidURL, phase, showStatus, statusProps };
};

export { useModelLoader, IN_FLIGHT_PHASES, formatElapsed };
